import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import {
  OrgUnitBasic,
  OrgUnitSearch,
} from 'app/core/models/org-unit/org-unit-search';
import { OrgUnitTypeForViewing } from 'app/core/models/org-unit/org-unit-type-for-viewing';
import { Observable, catchError, map, throwError } from 'rxjs';

export interface ExtOrgUnitPerson {
  personId: number;
  personName: string;
  position: string;
  station: string;
  supervisor: string;
  userId: string;
  username: string;
  watch: string;
}

export interface OrgUnitTypeForId {
  description: string;
  externalId: number;
  id: number;
  isRequired: boolean;
  name: string;
  source: string;
}

export interface OrgUnitLookup {
  hierarchyKey: string;
  key: string;
  name: string;
  parentKey: string;
}

export interface OrganisationUnit {
  id: number;
  parentId: number;
  orgUnitTypeId: number;
  externalId: number;
  externalParentId: number;
  name: string;
  externalExId: string;
  description: string;
  isActive: boolean;
  existsInJobRole?: boolean;
}

export interface OrgUnitSearchParameters {
  isViewUserData?: boolean; //When true, returns the caller's OUs for the viewing org unit type (or OrgUnitTypeId if provided)
  isViewUserPermissions?: boolean; //When true, returns all the caller's permission OUs (or only for OrgUnitTypeId if provided)
  isOrderByTypeWhenNoSearch?: boolean; //When true, returns the results ordered by OU type name then OU name (when no Search)
  isIncludeInactive?: boolean; //When true, inactive OUs are returned in the search results, but N.B. inactive OUs are always returned in the parent dictionary
  allowSessionAdmin?: boolean; // When true, considers a session admin as a full admin when computing one of the 'IsViewUser' options
  orgUnitTypeId?: number;
  source?: string; //e.g. "CSOD" - requires ExternalTypeId to be included, and only used when OrgUnitTypeId is not given
  externalTypeId?: string; //e.g. providing "32" or "Location" would return the same result etc
  search?: string; //can always be set to filter the results by name
  maxCount?: number; //When > 0, limits the number of results returned
}

const dotNetApiServerUri = environment.serverUrl;

@Injectable({
  providedIn: 'root',
})
export class OrgUnitService {
  private readonly http = inject(HttpClient);

  getOrgUnitTypes(): Observable<OrgUnitLookup[]> {
    let url = `${dotNetApiServerUri}/orgUnitTypes/lookups`;

    return this.http.get<OrgUnitLookup[]>(url).pipe(
      map((resp) => resp),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getLookups(entity: string, parentKey?: string): Observable<OrgUnitLookup[]> {
    let url = `${dotNetApiServerUri}/lookups/${entity}`;
    if (parentKey) {
      url = `${url}/${parentKey}`;
    }
    return this.http.get<OrgUnitLookup[]>(url).pipe(
      map((resp) => resp),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getOrgUnitsByTypeId(orgUnitTypeId: string): Observable<OrganisationUnit[]> {
    const url = `${dotNetApiServerUri}/orgUnits/${orgUnitTypeId}`;
    return this.http.get<OrganisationUnit[]>(url).pipe(
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getOrgUnitTypeById(typeId: number | string): Observable<OrgUnitTypeForId> {
    const url = `${dotNetApiServerUri}/orgUnitTypes/${typeId}`;
    return this.http.get<OrgUnitTypeForId>(url).pipe(
      map((resp) => resp),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getOrgUnitsForViewing(): Observable<OrganisationUnit[]> {
    const url = `${dotNetApiServerUri}/orgUnits/forViewing`;
    return this.http.get<OrganisationUnit[]>(url).pipe(
      map((resp) => resp),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getOrgUnitTypesForViewing(): Observable<OrgUnitTypeForViewing> {
    const url = `${dotNetApiServerUri}/orgUnitTypes/forViewing`;
    return this.http.get<OrgUnitTypeForViewing>(url).pipe(
      map((resp) => resp),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getOrgUnitLookups(
    orgUnitLookup: OrgUnitSearchParameters,
  ): Observable<OrgUnitLookup[]> {
    const url = `${dotNetApiServerUri}/orgUnits/lookups`;
    return this.http.post<OrgUnitLookup[]>(url, orgUnitLookup).pipe(
      map((resp) => resp),
      catchError((err) => {
        return throwError(() => new Error(err));
      }),
    );
  }

  getOrgUnitSearch(
    orgUnitLookup: OrgUnitSearchParameters,
  ): Observable<OrgUnitSearch> {
    const url = `${dotNetApiServerUri}/orgUnits/search`;
    return this.http.post<OrgUnitSearch>(url, orgUnitLookup).pipe(
      map((response) => {
        return {
          orgUnits: response.orgUnits
            .map((ou) => {
              return {
                ...ou,
                ouHierarchy: this.getHierarchyName(ou, response.parentOrgUnits),
              };
            })
            .sort((a, b) => a.ouHierarchy.localeCompare(b.ouHierarchy)),
          parentOrgUnits: response.parentOrgUnits,
        };
      }),
    );
  }

  getHierarchyName(
    ou: OrgUnitBasic,
    parentOrgUnits: { [id: string]: OrgUnitBasic },
  ): string {
    const parentId = ou.parentId;
    if (!parentId) {
      return `${ou.name} ${ou.isActive === false ? '(Inactive)' : ''}`;
    }

    const parent = parentOrgUnits[parentId];
    if (parent) {
      return `${this.getHierarchyName(parent, parentOrgUnits)} > ${ou.name} ${ou.isActive === false ? '(Inactive)' : ''}`;
    }

    return ou.name;
  }
}
